import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export const TEMPLATE_KEYWORDS_QUERY_KEY = 'templateKeywords';
export const TEMPLATE_KEYWORDS_FOR_ACCOUNT_QUERY_KEY = 'templateKeywordsForAccount';

export const useTemplateKeywords = ({
	industry,
	enabled = true,
	templateTypes,
}: {
	industry: Api.Industry;
	enabled?: boolean;
	templateTypes?: Api.TemplateType[];
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			let url = `template/${encodeURIComponent(industry)}/keywords`;
			if (templateTypes?.length) {
				const params = new URLSearchParams();
				templateTypes.forEach(t => params.append('templateTypes', String(t)));
				url = `${url}?${params.toString()}`;
			}
			const value = await userSession.webServiceHelper.callAsync<string[]>(url, 'GET');
			return value;
		},
		queryKey: [TEMPLATE_KEYWORDS_QUERY_KEY, industry, templateTypes],
	});
};

export const invalidateTemplateKeywords = ({
	industry,
	templateTypes,
}: {
	industry: Api.Industry;
	templateTypes?: Api.TemplateType[];
}) => {
	reactQueryClient.invalidateQueries({ queryKey: [TEMPLATE_KEYWORDS_QUERY_KEY, industry, templateTypes] });
};

/**
 * Fetches keywords for a specific account when impersonating.
 * This returns account-specific keywords instead of industry-wide keywords.
 */
export const useTemplateKeywordsForAccount = ({
	impersonationContext,
	enabled = true,
}: {
	impersonationContext: Api.IImpersonationContext;
	enabled?: boolean;
}) => {
	const userSession = useUserSession();
	const accountId = impersonationContext?.account?.id;

	return useQuery({
		enabled: enabled && !!accountId,
		queryFn: async () => {
			const url = Api.ImpersonationBroker.composeApiUrl({
				urlPath: 'template/keywords',
				impersonationContext,
			});
			const value = await userSession.webServiceHelper.callAsync<string[]>(url, 'GET');
			return value;
		},
		queryKey: [TEMPLATE_KEYWORDS_FOR_ACCOUNT_QUERY_KEY, accountId],
	});
};

export const invalidateTemplateKeywordsForAccount = ({ accountId }: { accountId: string }) => {
	reactQueryClient.invalidateQueries({ queryKey: [TEMPLATE_KEYWORDS_FOR_ACCOUNT_QUERY_KEY, accountId] });
};
